import { useEffect, useMemo, useState } from 'react';
import { X, Network, Server, Wifi, MapPin, Package, Check, AlertTriangle, Loader2 } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { CustomDropdown } from '../../components/CustomDropdown ';
import { jwt_decode } from '../../utils/jwt_decode';
import { useSearchParams } from 'react-router-dom';

type Mode = 'add' | 'edit';

export interface Asset {
    id: number;
    name: string;
    asset_type: string;
    hostname: string;
    ip_address: string;
    location: string;
    switch_role: string | null;
    total_ports: number | null;
    is_primary: boolean;
    tags: string;
    purchase_date?: string | null;
    installation_date?: string | null;
    warranty_expiration?: string | null;
    warranty_details?: string | null;
    amc_expiration?: string | null;
    amc_provider?: string | null;
    amc_details?: string | null;
    end_of_life?: string | null;
    end_of_support?: string | null;
}

interface AddAssetModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    mode?: Mode;
    asset?: Asset | null;
}

const emptyForm = {
    name: '',
    asset_type: 'camera',
    hostname: '',
    ip_address: '',
    location: '',
    switch_role: '',
    total_ports: '',
    is_primary: false,
    tags: '',
    purchase_date: '',
    installation_date: '',
    warranty_expiration: '',
    warranty_details: '',
    amc_expiration: '',
    amc_provider: '',
    amc_details: '',
    end_of_life: '',
    end_of_support: ''
};

const isValidIPv4 = (ip: string): boolean => {
    const ipv4Regex =
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipv4Regex.test(ip);
};

const tagsToComma = (tags: any): string => {
    try {
        if (!tags) return '';
        const parsed = typeof tags === 'string' ? JSON.parse(tags) : tags;
        return Array.isArray(parsed) ? parsed.join(', ') : '';
    } catch {
        return '';
    }
};

const commaToTagsJson = (tags: string): string => {
    const arr = tags
        .split(',')
        .map(t => t.trim())
        .filter(Boolean);
    return JSON.stringify(arr);
};

const AddAssetModal: React.FC<AddAssetModalProps> = ({
    open,
    onClose,
    onSuccess,
    mode = 'add',
    asset = null
}) => {
    const [formData, setFormData] = useState(emptyForm);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const decodedToken = jwt_decode();
    const [searchParams] = useSearchParams();
    const organizationId = decodedToken?.organization_id;
    const clientId = searchParams.get('client_id');

    const showPortFields = useMemo(() => ['switch', 'router', 'firewall', 'unmanaged_switch'].includes(formData.asset_type), [formData.asset_type]);
    const showPrimaryOption = useMemo(() => formData.asset_type === 'switch' && formData.switch_role === 'core', [formData.asset_type, formData.switch_role]);

    useEffect(() => {
        if (!open) return;

        if (mode === 'edit' && asset) {
            setFormData({
                name: asset.name ?? '',
                asset_type: asset.asset_type ?? 'camera',
                hostname: asset.hostname ?? '',
                ip_address: asset.ip_address ?? '',
                location: asset.location ?? '',
                switch_role: asset.switch_role ?? '',
                total_ports: asset.total_ports != null ? String(asset.total_ports) : '',
                is_primary: asset.is_primary ?? false,
                tags: tagsToComma(asset.tags),
                purchase_date: asset.purchase_date ?? '',
                installation_date: asset.installation_date ?? '',
                warranty_expiration: asset.warranty_expiration ?? '',
                warranty_details: asset.warranty_details ?? '',
                amc_expiration: asset.amc_expiration ?? '',
                amc_provider: asset.amc_provider ?? '',
                amc_details: asset.amc_details ?? '',
                end_of_life: asset.end_of_life ?? '',
                end_of_support: asset.end_of_support ?? ''
            });
        } else {
            setFormData(emptyForm);
        }

        setErrors({});
    }, [open, mode, asset]);

    const assetTypes = [
        { label: 'Camera', value: 'camera' },
        { label: 'NVR', value: 'nvr' },
        { label: 'Switch', value: 'switch' },
        { label: 'Unmanaged Switch', value: 'unmanaged_switch' },
        { label: 'Firewall', value: 'firewall' }
    ];

    const switchRoles = [
        { label: 'Core', value: 'core' },
        { label: 'Distribution', value: 'distribution' },
        { label: 'Access', value: 'access' },
        { label: 'Edge', value: 'edge' }
    ];

    const handleClose = () => {
        setFormData(emptyForm);
        setErrors({});
        onClose();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        if (errors[name]) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }

        if (name === 'total_ports') {
            if (value === '') {
                setFormData(prev => ({ ...prev, total_ports: '' }));
                return;
            }
            const n = Number(value);
            if (!Number.isFinite(n) || n < 0) return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validateForm = (): boolean => {
        const next: Record<string, string> = {};

        if (!formData.name.trim()) next.name = 'Name is required';

        const ip = formData.ip_address.trim();
        if (formData.asset_type !== 'unmanaged_switch') {
            if (!ip) next.ip_address = 'IP Address is required';
            else if (!isValidIPv4(ip)) next.ip_address = 'Invalid IPv4 address';
        }

        if (showPortFields && formData.total_ports) {
            const ports = Number(formData.total_ports);
            if (!Number.isInteger(ports) || ports < 1 || ports > 999) next.total_ports = 'Ports must be 1 to 999';
        }

        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) {
            showError('Please fix validation errors');
            return;
        }

        try {
            setLoading(true);

            const payload: any = {
                client_id: Number(clientId),
                organization_id: Number(organizationId),
                name: formData.name.trim(),
                asset_type: formData.asset_type,
                hostname: formData.hostname?.trim() || null,
                ip_address: formData.ip_address.trim(),
                location: formData.location?.trim() || null,
                tags: commaToTagsJson(formData.tags || ''),
                status: 'active'
            };

            if (['switch', 'router', 'firewall', 'unmanaged_switch'].includes(formData.asset_type)) {
                payload.switch_role = formData.switch_role || null;
                payload.total_ports = formData.total_ports ? Number(formData.total_ports) : null;

                if (formData.switch_role === 'core') {
                    payload.is_primary = formData.is_primary;
                } else {
                    payload.is_primary = false;
                }
            }

            // Lifecycle & Warranty Fields
            payload.purchase_date = formData.purchase_date || null;
            payload.installation_date = formData.installation_date || null;
            payload.warranty_expiration = formData.warranty_expiration || null;
            payload.warranty_details = formData.warranty_details?.trim() || null;
            payload.amc_expiration = formData.amc_expiration || null;
            payload.amc_provider = formData.amc_provider?.trim() || null;
            payload.amc_details = formData.amc_details?.trim() || null;
            payload.end_of_life = formData.end_of_life || null;
            payload.end_of_support = formData.end_of_support || null;

            const response =
                mode === 'edit' && asset?.id
                    ? await baseapi.put(`/assets/${asset.id}`, payload)
                    : await baseapi.post('/assets', payload);

            if (response.data.status) {
                showSuccess(mode === 'edit' ? 'Asset updated successfully' : 'Asset added successfully');
                onSuccess();
                handleClose();
            } else {
                showError(response.data.message || 'Failed');
            }
        } catch (error: any) {
            showError(error.message || 'Failed');
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <>
            {/* Backdrop Overlay */}
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[9998]" onClick={handleClose} />

            {/* Modal Content Wrapper */}
            <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
                <div className="w-full max-w-3xl bg-white border border-slate-200 shadow-2xl rounded-2xl text-slate-900 relative overflow-hidden max-h-[90vh] overflow-y-auto custom-scroll animate-in zoom-in-95 duration-200">

                    {/* Top gradient indicator line */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#422568]/20 via-[#422568] to-[#422568]/20" />

                    {/* Header */}
                    <div className="sticky top-0 bg-white/90 backdrop-blur-md border-b border-slate-100 px-5 py-3.5 z-10 flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                            <div className="p-1.5 bg-[#422568] rounded-lg">
                                <Network className="h-4.5 w-4.5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xs font-black uppercase tracking-widest">
                                    {mode === 'edit' ? 'Edit Asset' : 'Add New Asset'}
                                </h2>
                                <p className="text-[9px] text-slate-500 font-bold uppercase tracking-tighter mt-0.5">
                                    Infrastructure Configuration
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-50 rounded-full transition-all"
                        >
                            <X className="w-4.5 h-4.5" />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-5">
                        <div className="space-y-4">

                            {/* Row 1: Basic Info (Name, Asset Type) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="flex items-center text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">
                                        <Server className="w-3 h-3 mr-1 text-blue-500" />
                                        Name <span className="text-rose-500 ml-0.5">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className={`w-full bg-slate-50 border ${errors.name ? 'border-rose-400 focus:ring-rose-500/10 focus:border-rose-500' : 'border-slate-200 focus:ring-[#422568]/10 focus:border-[#422568]'} rounded-lg px-2.5 py-1.5 text-[11px] font-bold text-slate-900 focus:outline-none focus:ring-2 transition-all`}
                                        placeholder="Core-Switch-01"
                                    />
                                    {errors.name && (
                                        <p className="mt-0.5 text-[9px] text-rose-500 font-bold flex items-center">
                                            <AlertTriangle className="w-3 h-3 mr-1" /> {errors.name}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="flex items-center text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">
                                        <Network className="w-3 h-3 mr-1 text-purple-500" />
                                        Asset Type <span className="text-rose-500 ml-0.5">*</span>
                                    </label>
                                    <CustomDropdown
                                        data={assetTypes}
                                        width="w-full"
                                        selectedValue={formData.asset_type}
                                        placeholder="Select type"
                                        onSelect={(value: string) => setFormData(prev => ({
                                            ...prev,
                                            asset_type: value,
                                            ip_address: value === 'unmanaged_switch' ? '' : prev.ip_address
                                        }))}
                                        buttonHeight={10}
                                    />
                                </div>
                            </div>

                            {/* Row 2: Network Info (IP Address, Hostname) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {formData.asset_type !== 'unmanaged_switch' && (
                                    <div>
                                        <label className="flex items-center text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">
                                            <Wifi className="w-3 h-3 mr-1 text-emerald-500" />
                                            IP Address <span className="text-rose-500 ml-0.5">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="ip_address"
                                            value={formData.ip_address}
                                            onChange={handleInputChange}
                                            className={`w-full bg-slate-50 border ${errors.ip_address ? 'border-rose-400 focus:ring-rose-500/10 focus:border-rose-500' : 'border-slate-200 focus:ring-[#422568]/10 focus:border-[#422568]'} rounded-lg px-2.5 py-1.5 text-[11px] font-bold text-slate-900 focus:outline-none focus:ring-2 transition-all font-mono`}
                                            placeholder="192.168.1.10"
                                        />
                                        {errors.ip_address && (
                                            <p className="mt-0.5 text-[9px] text-rose-500 font-bold flex items-center">
                                                <AlertTriangle className="w-3 h-3 mr-1" /> {errors.ip_address}
                                            </p>
                                        )}
                                    </div>
                                )}

                                <div className={formData.asset_type === 'unmanaged_switch' ? 'md:col-span-2' : ''}>
                                    <label className="flex items-center text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">
                                        <Server className="w-3 h-3 mr-1 text-cyan-500" />
                                        Hostname
                                    </label>
                                    <input
                                        type="text"
                                        name="hostname"
                                        value={formData.hostname}
                                        onChange={handleInputChange}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-[11px] font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all"
                                        placeholder="core-sw-01.local"
                                    />
                                </div>
                            </div>

                            {/* Row 3: Location & Tags */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="flex items-center text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">
                                        <MapPin className="w-3 h-3 mr-1 text-amber-500" />
                                        Location
                                    </label>
                                    <input
                                        type="text"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleInputChange}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-[11px] font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all"
                                        placeholder="Server Room Rack 1"
                                    />
                                </div>

                                <div>
                                    <label className="flex items-center text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">
                                        <Package className="w-3.5 h-3.5 mr-1 text-rose-500" />
                                        Tags
                                    </label>
                                    <input
                                        type="text"
                                        name="tags"
                                        value={formData.tags}
                                        onChange={handleInputChange}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-[11px] font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all"
                                        placeholder="critical, network"
                                    />
                                </div>
                            </div>

                            {/* Configuration for Switch, Router, Firewall */}
                            {showPortFields && (
                                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                                        {/* Switch Role - Only show for switch / unmanaged switch */}
                                        {['switch', 'unmanaged_switch'].includes(formData.asset_type) && (
                                            <div>
                                                <label className="flex items-center text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">
                                                    <Network className="w-3 h-3 mr-1 text-violet-500" />
                                                    Switch Role
                                                </label>
                                                <CustomDropdown
                                                    data={switchRoles}
                                                    width="w-full"
                                                    selectedValue={formData.switch_role || ''}
                                                    placeholder="Select role"
                                                    onSelect={(value: string) => setFormData(prev => ({ ...prev, switch_role: value }))}
                                                    buttonHeight={10}
                                                />
                                            </div>
                                        )}

                                        {/* Total Ports - Show for switch, router, and firewall */}
                                        <div className={!['switch', 'unmanaged_switch'].includes(formData.asset_type) ? "md:col-span-2" : ""}>
                                            <label className="flex items-center text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">
                                                <Network className="w-3 h-3 mr-1 text-sky-500" />
                                                Total Ports
                                            </label>
                                            <input
                                                type="number"
                                                name="total_ports"
                                                value={formData.total_ports}
                                                onChange={handleInputChange}
                                                min={1}
                                                max={999}
                                                className={`w-full bg-slate-50 border ${errors.total_ports ? 'border-rose-400 focus:ring-rose-500/10 focus:border-rose-500' : 'border-slate-200 focus:ring-[#422568]/10 focus:border-[#422568]'} rounded-lg px-2.5 py-1.5 text-[11px] font-bold text-slate-900 focus:outline-none focus:ring-2 transition-all font-mono`}
                                                placeholder="24"
                                            />
                                            {errors.total_ports && (
                                                <p className="mt-0.5 text-[9px] text-rose-500 font-bold flex items-center">
                                                    <AlertTriangle className="w-3 h-3 mr-1" /> {errors.total_ports}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Primary Switch Toggle - Only visible for Core Switches */}
                                    {showPrimaryOption && (
                                        <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                                            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                                                Is Primary Core Switch?
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, is_primary: !prev.is_primary }))}
                                                className={`w-10 h-5 rounded-full relative transition-all ${formData.is_primary ? 'bg-[#422568]' : 'bg-slate-200'}`}
                                            >
                                                <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${formData.is_primary ? 'left-6' : 'left-1'}`} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Section: Warranty, AMC & Lifecycle */}
                            <div className="pt-4 border-t border-slate-100 space-y-3">
                                <h3 className="text-[10px] font-black text-[#422568] uppercase tracking-widest">
                                    Warranty, AMC & Lifecycle
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {/* Row 1: Warranty Dates */}
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Purchase Date</label>
                                        <input
                                            type="date"
                                            name="purchase_date"
                                            value={formData.purchase_date}
                                            onChange={handleInputChange}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Installation Date</label>
                                        <input
                                            type="date"
                                            name="installation_date"
                                            value={formData.installation_date}
                                            onChange={handleInputChange}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Warranty Exp</label>
                                        <input
                                            type="date"
                                            name="warranty_expiration"
                                            value={formData.warranty_expiration}
                                            onChange={handleInputChange}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all"
                                        />
                                    </div>

                                    {/* Row 2: AMC Info */}
                                    <div className="md:col-span-2">
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block mb-1">AMC Provider</label>
                                        <input
                                            type="text"
                                            name="amc_provider"
                                            value={formData.amc_provider}
                                            onChange={handleInputChange}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-[10px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all"
                                            placeholder="Service Provider Name"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block mb-1">AMC Expiration</label>
                                        <input
                                            type="date"
                                            name="amc_expiration"
                                            value={formData.amc_expiration}
                                            onChange={handleInputChange}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all"
                                        />
                                    </div>

                                    {/* Row 3: Product Lifecycle */}
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block mb-1">End of Life (EoL)</label>
                                        <input
                                            type="date"
                                            name="end_of_life"
                                            value={formData.end_of_life}
                                            onChange={handleInputChange}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block mb-1">End of Support (EoS)</label>
                                        <input
                                            type="date"
                                            name="end_of_support"
                                            value={formData.end_of_support}
                                            onChange={handleInputChange}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all"
                                        />
                                    </div>
                                    {/* Empty cell to balance layout */}
                                    <div className="hidden md:block" />
                                </div>

                                {/* Textareas side-by-side */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Warranty Details</label>
                                        <textarea
                                            name="warranty_details"
                                            value={formData.warranty_details}
                                            onChange={handleInputChange}
                                            rows={2}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-[10px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all resize-none h-12"
                                            placeholder="Enter warranty coverage details..."
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block mb-1">AMC Details</label>
                                        <textarea
                                            name="amc_details"
                                            value={formData.amc_details}
                                            onChange={handleInputChange}
                                            rows={2}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-[10px] font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all resize-none h-12"
                                            placeholder="Enter AMC terms..."
                                        />
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* Footer Actions */}
                        <div className="flex justify-end gap-2.5 mt-5 pt-3 border-t border-slate-100">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className="px-3.5 py-1.5 text-[10px] font-black text-slate-500 hover:text-slate-700 uppercase tracking-widest transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-5 py-1.5 bg-[#422568] text-white rounded-lg text-[10px] font-black shadow-lg shadow-[#422568]/20 hover:bg-[#422568] transition-all active:scale-95 flex items-center gap-1.5 uppercase tracking-widest disabled:opacity-50"
                            >
                                {loading ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                    <Check className="w-3 h-3" />
                                )}
                                {mode === 'edit' ? 'Update' : 'Add'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AddAssetModal;

